import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@devhop/ui";
import { useEffect, useState } from "react";
import type { UpdateEventPayload } from "@/modules/events/presentation/api/client";
import type { AdminEventListItem } from "@/modules/events/presentation/api/queries";
import {
  useDeleteAdminEventMutation,
  useUpdateAdminEventMutation,
} from "@/modules/events/presentation/api/queries";
import type { SimpleSelectOption } from "@/shared/ui/SimpleSelect";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const STATUS_OPTIONS: SimpleSelectOption[] = [
  { value: "draft", label: "draft — ພາບຍ້ອນ" },
  { value: "scoring", label: "scoring — ແຂ່ງຢູ່" },
  { value: "closed", label: "closed — ປິດແລ້ວ" },
  { value: "published", label: "published — ປະກາດ" },
];

type Props = {
  event: AdminEventListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
};

export function EditEventDialog({
  event,
  open,
  onOpenChange,
  onDeleted,
}: Props) {
  const updateMutation = useUpdateAdminEventMutation(event?.id);
  const deleteMutation = useDeleteAdminEventMutation(event?.id);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [venue, setVenue] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [status, setStatus] = useState<string>("draft");
  const [judge1Pin, setJudge1Pin] = useState("");
  const [judge2Pin, setJudge2Pin] = useState("");
  const [judge3Pin, setJudge3Pin] = useState("");

  useEffect(() => {
    if (!event || !open) return;
    setName(event.name);
    setSlug(event.slug);
    setVenue(event.venue ?? "");
    setEventDate(event.eventDate ?? "");
    setStatus(event.status);
    setJudge1Pin("");
    setJudge2Pin("");
    setJudge3Pin("");
  }, [event, open]);

  const handleSubmit = async () => {
    if (!event) return;
    const n = name.trim();
    const s = slug.trim().toLowerCase();
    if (!n || !s) {
      toast.error("ກະລຸນາໃສ່ຊື່ ແລະ slug");
      return;
    }
    if (!SLUG_REGEX.test(s)) {
      toast.error("slug ບໍ່ຖືກຕ້ອງ — ໃຊ້ເຄົ່ອງເລັກພິເສດ ຂີດກັນຂົກຢ່າງດຽວ");
      return;
    }

    let eventDateSent: string | null | undefined;
    const d = eventDate.trim();
    if (d === "") {
      eventDateSent =
        event.eventDate != null && event.eventDate !== "" ? null : undefined;
    } else {
      eventDateSent = d;
    }

    const venueTrim = venue.trim();
    const patch: UpdateEventPayload = {
      name: n,
      slug: s,
      status: status as "draft" | "scoring" | "closed" | "published",
      venue: venueTrim === "" ? null : venueTrim,
    };
    if (eventDateSent !== undefined) patch.eventDate = eventDateSent;

    const p1 = judge1Pin.trim();
    const p2 = judge2Pin.trim();
    const p3 = judge3Pin.trim();
    if (p1) patch.judge1Pin = p1;
    if (p2) patch.judge2Pin = p2;
    if (p3) patch.judge3Pin = p3;

    try {
      await updateMutation.mutateAsync(patch);
      onOpenChange(false);
    } catch {
      /* mutation toasts error */
    }
  };

  if (!event) return null;

  const isBusy = updateMutation.isPending || deleteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ແກ້ໄຂງານ</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-1">
          <div className="flex flex-col gap-2">
            <Label htmlFor="ee-name">ຊື່ງານ</Label>
            <Input
              id="ee-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ee-slug">Slug</Label>
            <Input
              id="ee-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ee-venue">ສະຖານທີ່</Label>
            <Input
              id="ee-venue"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ee-date">ວັນທີ</Label>
            <Input
              id="ee-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>ສະຖານະ</Label>
            <SimpleSelect
              value={status}
              onValueChange={setStatus}
              className="w-full"
              options={STATUS_OPTIONS}
            />
          </div>

          <p className="mt-2 font-medium text-foreground text-sm">
            ຣີເຊັດ PIN ກຳມະການ (ເວັ້ນວ່າງ = ບໍ່ປ່ຽນ)
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="ee-j1">ກຳມະການ 1</Label>
              <Input
                id="ee-j1"
                type="password"
                autoComplete="new-password"
                value={judge1Pin}
                onChange={(e) => setJudge1Pin(e.target.value)}
                placeholder="ໃໝ່..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="ee-j2">ກຳມະການ 2</Label>
              <Input
                id="ee-j2"
                type="password"
                autoComplete="new-password"
                value={judge2Pin}
                onChange={(e) => setJudge2Pin(e.target.value)}
                placeholder="ໃໝ່..."
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="ee-j3">ກຳມະການ 3</Label>
              <Input
                id="ee-j3"
                type="password"
                autoComplete="new-password"
                value={judge3Pin}
                onChange={(e) => setJudge3Pin(e.target.value)}
                placeholder="ໃໝ່..."
              />
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <p className="font-semibold text-destructive text-sm">ໂຊນອັນຕະລາຍ</p>
            <p className="mt-1 text-muted-foreground text-xs">
              ການລຶບງານຈະລຶບທີມ, ຄະແນນ ແລະຂໍ້ມູນທັງໝົດໃນ event ນີ້ຖາວອນ (cascade).
            </p>

            <div className="mt-3">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive" disabled={isBusy}>
                    ລຶບງານນີ້
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>ລຶບງານ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      ທ່ານກຳລັງຈະລຶບ <strong>{event.name}</strong> — ການກະທຳນີ້
                      ບໍ່ສາມາດຍ້ອນກັບໄດ້.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={isBusy}>
                      ຍົກເລີກ
                    </AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      disabled={isBusy}
                      onClick={async () => {
                        try {
                          await deleteMutation.mutateAsync();
                          onOpenChange(false);
                          onDeleted?.();
                        } catch {
                          /* mutation toasts error */
                        }
                      }}
                    >
                      {deleteMutation.isPending ? "ກຳລັງລຶບ..." : "ລຶບ"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isBusy}
          >
            ຍົກເລີກ
          </Button>
          <Button onClick={handleSubmit} disabled={isBusy}>
            {updateMutation.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
